import classNames from 'classnames';
import { Variants, m as motion, useAnimation } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Autoplay, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Reason } from '@/entities/reason';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import OrientationImage from '@/shared/components/shared/OrientationImage';
import Pagination from '@/shared/components/shared/Pagination';
import Responsive from '@/shared/components/shared/Responsive';
import { useMounted } from '@/shared/hooks/use-mounted';
import { easeInOutSine } from '@/shared/lib/utils/easings';

export type FullscreenSliderProps = {
    slides: Reason[];
};

const SECTION_ID = 'cause';

type Props = React.HTMLAttributes<HTMLElement> & {
    fullscreenSliderProps: FullscreenSliderProps;
};

function FullscreenSlider({ fullscreenSliderProps, ...props }: Props) {
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const [lastRealIndex, setLastRealIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const controls = useAnimation();
    const numSlides = fullscreenSliderProps.slides.length;
    const device = useMediaQueryDeviceStateValue();
    const isMobile = device.includes('mobile');

    const textVariants: Variants = {
        initial: { y: -50, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: 50, opacity: 0 },
    };

    const isMounted = useMounted();

    useEffect(() => {
        if (isFirstLoad) {
            setIsFirstLoad(false);
            return;
        }

        if (currentRealIndex === 0 && lastRealIndex === numSlides - 1) {
            setDirection(1);
        } else if (currentRealIndex === numSlides - 1 && lastRealIndex === 0) {
            setDirection(-1);
        } else if (currentRealIndex > lastRealIndex) {
            setDirection(1);
        } else if (currentRealIndex < lastRealIndex) {
            setDirection(-1);
        }

        controls.start({
            scale: [1.1, 1.1],
            x: direction > 0 ? [0, isMobile ? 30 : 70, 0] : [0, isMobile ? -30 : -70, 0],
            transition: {
                duration: 0.8,
                ease: easeInOutSine,
            },
        });
    }, [currentRealIndex, lastRealIndex, numSlides, direction, isFirstLoad, controls, isMobile]);

    return (
        <section
            {...props}
            className={classNames('fullscreen-slider-section js-header-theme-trigger', props.className)}
            data-theme="dark"
            id={props.id ?? SECTION_ID}
        >
            <div className="fullscreen-slider-bottom">
                {isMounted && (
                    <>
                        <Swiper
                            className="fullscreen-slider"
                            modules={[Navigation, Autoplay]}
                            autoplay={{
                                delay: 6000,
                            }}
                            centeredSlides
                            navigation={{
                                prevEl: '.fullscreen-slider-prev-btn',
                                nextEl: '.fullscreen-slider-next-btn',
                            }}
                            speed={1300}
                            spaceBetween={0}
                            onRealIndexChange={(swiper) => {
                                const newRealIndex = swiper.realIndex;
                                setLastRealIndex(currentRealIndex);
                                setCurrentRealIndex(newRealIndex);
                            }}
                            loop={true}
                        >
                            {fullscreenSliderProps.slides.map((slide, i) => (
                                <SwiperSlide className="fullscreen-slider-slide" key={i}>
                                    <Responsive className="fullscreen-slider-img">
                                        <motion.div
                                            style={{ scale: 1.1 }}
                                            animate={controls}
                                            className="responsive__item fullscreen-slider-img-inner"
                                        >
                                            <OrientationImage
                                                className="responsive__item"
                                                sizes="100vw"
                                                sources={{
                                                    horizontal: slide.img,
                                                    vertical: slide.mobileImg,
                                                }}
                                            />
                                        </motion.div>
                                    </Responsive>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {fullscreenSliderProps.slides.map((item, index) => {
                            const isCurrent = index === currentRealIndex;
                            const baseDelay = isCurrent ? 0.7 : 0;

                            return (
                                <div key={index} className="fulscreen-slider-text">
                                    <div className="fulscreen-slider-text-inner">
                                        <motion.div
                                            key={`title-${index}`}
                                            className="fulscreen-slider-title h3"
                                            variants={textVariants}
                                            initial="initial"
                                            animate={isCurrent ? 'animate' : 'exit'}
                                            exit="exit"
                                            transition={{ duration: 0.5, ease: 'easeInOut', delay: baseDelay }}
                                        >
                                            {item.text}
                                        </motion.div>
                                        {item.detail && (
                                            <motion.div
                                                key={`description-${index}`}
                                                className="fulscreen-slider-description text-m"
                                                variants={textVariants}
                                                initial="initial"
                                                animate={isCurrent ? 'animate' : 'exit'}
                                                exit="exit"
                                                transition={{
                                                    duration: 0.5,
                                                    ease: 'easeInOut',
                                                    delay: baseDelay + 0.05,
                                                }}
                                            >
                                                {item.detail}
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {numSlides > 1 && (
                            <div className="fullscreen-slider-pagination-wrapper swiper__wrapper">
                                <Pagination
                                    numSlides={numSlides}
                                    currentRealIndex={currentRealIndex}
                                    lastRealIndex={lastRealIndex}
                                    prevElClass="fullscreen-slider-prev-btn"
                                    nextElClass="fullscreen-slider-next-btn"
                                    wrapperClass="fullscreen-slider-pagination wrapper"
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}

export default FullscreenSlider;
